<script setup lang="ts">
    import { InputRow, Select, Combobox } from '@/components/ui/form';
    import { Textarea } from '@/components/ui/textarea';
    import { Voucher, FileItem, VoucherFile } from '@/types';
    import { optionsYesNo } from '@/utils/form';
    import FileUploadZone from '@/components/FileUploadZone.vue';
    import { Label } from '@/components/ui/label';
    import { useI18n } from 'vue-i18n';
    import { Alert } from '@/components/ui/alert';
    import { useVoucherApprovedAccountsData } from '@/composables/useVoucherApprovedAccountsData';
    import { computed, ref, watch, watchEffect } from 'vue';
    import { useVoucherFileData } from '@/composables/useVoucherFileData';
    import { useEconomicDimensionsData } from '@/composables/useEconomicDimensionsData';
    import axios from 'axios';
    import { formatDate, formatCurrency } from '@/utils/helpers';
    import Loading from '@/components/Loading.vue';
    import type { EconomicDimension, EconomicDimensionValue } from '@/types';

    const { t } = useI18n();

    const MAX_UPLOADS = 5;
    const MAX_SIZE = 9 * 1024 * 1024;

    const props = defineProps<{
        voucher: Voucher;
        form: any;
    }>();

    const { approvedAccounts, isPendingApprovedAccounts } = useVoucherApprovedAccountsData(
        props.voucher.company_id,
    );

    const { files, isPlaceholderDataFiles } = useVoucherFileData(props.voucher.id);

    const { dimensions, isPendingDimensions } = useEconomicDimensionsData(props.voucher.company_id);

    const enabledDimensions = computed(() => {
        if (!dimensions.value) return [];

        return dimensions.value.filter((d: EconomicDimension) => d.is_active && d.is_enabled);
    });

    const getDimensionValues = (dimension: EconomicDimension) => {
        if (!dimension.values) return [];
        return dimension.values
            .filter((v: EconomicDimensionValue) => v.is_active && v.is_enabled)
            .map((v: EconomicDimensionValue) => ({
                id: v.id,
                name: `${v.key} - ${v.name}`,
                value: v.id,
            }));
    };

    const voucherFiles = ref<FileItem[]>([]);

    watchEffect(() => {
        if (files.value?.length) {
            voucherFiles.value = files.value.map((file: any) => ({
                id: file.file_id,
                name: file.filename,
                url: file.url,
                size: file.size,
                uploading: false,
                uploaded: false,
                progress: 0,
                registered: true,
            }));
        }
    });

    watch(
        voucherFiles,
        (newFiles) => {
            const unregisteredFiles = newFiles
                .filter((file) => !file.registered)
                .map((file) => file.file)
                .filter(Boolean);

            props.form.file = unregisteredFiles;
        },
        { deep: true },
    );

    const handleFileDelete = async ({ id }: { id: number }) => {
        await axios.post(route('v2.voucher.delete'), { id: props.voucher.id, file_id: id });
    };

    // Initialize individual dimension fields from dimension_values array (full objects)
    watchEffect(() => {
        if (!enabledDimensions.value.length || !Array.isArray(props.form.dimension_values)) return;

        enabledDimensions.value.forEach((dimension: EconomicDimension) => {
            const dimensionValues = getDimensionValues(dimension);
            // Find matching dimension value object by ID
            const matchingValue = dimensionValues.find((dv: any) =>
                props.form.dimension_values.some((dvObj: any) => dvObj.id === dv.value)
            );
            // Always set the value, even if null, to ensure proper initialization
            props.form[`dimension_${dimension.id}`] = matchingValue?.value || null;
        });
    });

    // Watch individual dimension fields and sync to dimension_values array (full objects)
    watch(
        () => enabledDimensions.value.map((d: EconomicDimension) => props.form[`dimension_${d.id}`]),
        (newValues) => {
            const dimensionValueObjects: any[] = [];
            enabledDimensions.value.forEach((dimension: EconomicDimension, index: number) => {
                const value = newValues[index];
                if (value !== null && value !== undefined) {
                    // Get the full dimension value object from the dimension's values array
                    const fullDimensionValue = dimension.values?.find((v: EconomicDimensionValue) => v.id === value);
                    if (fullDimensionValue) {
                        dimensionValueObjects.push(fullDimensionValue);
                    }
                }
            });
            // Only update if the array actually changed to avoid unnecessary updates
            const currentValues = props.form.dimension_values || [];
            const currentIds = currentValues.map((v: any) => v.id).sort();
            const newIds = dimensionValueObjects.map((v: any) => v.id).sort();
            if (JSON.stringify(currentIds) !== JSON.stringify(newIds)) {
                props.form.dimension_values = dimensionValueObjects;
            }
        },
        { deep: true },
    );
</script>

<template>
    <Loading v-if="isPendingDimensions" />
    <div
        v-else
        class="space-y-5"
    >
        <div
            class="text-secondary border-secondary bg-secondary/5 flex flex-col gap-2.5 rounded-md border p-3.5 text-xs"
        >
            <div class="grid grid-cols-2 gap-3">
                <span>
                    {{ $t('vouchers.voucher_table.text') }}
                </span>
                <span class="font-bold">
                    {{ voucher.Text }}
                </span>
            </div>
            <div class="grid grid-cols-2 gap-3">
                <span>
                    {{ $t('vouchers.voucher_table.date') }}
                </span>
                <span class="font-bold">
                    {{ formatDate(voucher.Date) }}
                </span>
            </div>
            <div class="grid grid-cols-2 gap-3">
                <span>
                    {{ $t('vouchers.voucher_table.amount') }}
                </span>
                <span class="font-bold">
                    {{ formatCurrency(voucher.Amount) }}
                </span>
            </div>
        </div>

        <div class="flex flex-col gap-3">
            <InputRow
                :form="form"
                name="AccountHandle_Number"
                label="vouchers.account_handle"
            >
                <Combobox
                    v-model="form.AccountHandle_Number"
                    :options="approvedAccounts"
                    label-by="number_and_name"
                    value-by="accountNumber"
                    :show-remove-button="false"
                    :loading="isPendingApprovedAccounts"
                />
            </InputRow>

            <InputRow
                v-for="dimension in enabledDimensions"
                :key="dimension.id"
                :form="form"
                :name="`dimension_${dimension.id}`"
                :label="dimension.name"
            >
                <Combobox
                    v-model="form[`dimension_${dimension.id}`]"
                    :options="getDimensionValues(dimension)"
                    label-by="name"
                    value-by="value"
                    :show-remove-button="false"
                    :loading="isPendingDimensions"
                />
            </InputRow>

            <InputRow
                :form="form"
                name="comment"
                label="vouchers.comment_required"
            >
                <Textarea
                    id="comment"
                    v-model="form.comment"
                    :placeholder="$t('vouchers.comment')"
                />
            </InputRow>

            <InputRow
                :form="form"
                name="no_voucher"
                label="vouchers.lost"
            >
                <Select
                    v-model="form.no_voucher"
                    :options="optionsYesNo"
                    translate
                />
            </InputRow>

            <div
                class="flex flex-col gap-2"
                v-if="Number(form.no_voucher) === 0"
            >
                <Label for="upload_statement">
                    {{
                        t('vouchers.upload_statement', {
                            max_uploads: MAX_UPLOADS,
                        })
                    }}
                </Label>

                <FileUploadZone
                    v-model="voucherFiles"
                    :accepts-files="['.pdf', '.jpg', '.jpeg', '.png']"
                    multiple
                    :max-uploads="MAX_UPLOADS"
                    :max-size="MAX_SIZE"
                    :instant-upload="false"
                    :delete-file="handleFileDelete"
                />
            </div>

            <Alert
                v-if="Number(form.no_voucher) === 1"
                variant="warning"
            >
                {{ t('vouchers.comment_reason_lost') }}
            </Alert>
        </div>
    </div>
</template>
